import { defaultSessionView } from '../../analysis/sessionAnalysis/defaultSessionView'
import { selectStoredLaps } from '../../analysis/sessionAnalysis/lapSelection'
import type { ZoneVariancePoint } from '../../analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/consistencyGradient'
import type {
  TrackStats,
  TrackZone,
  ZoneAnalysisResult,
  ZoneLapData,
} from '../../analysis/zones/TrackZoneTypes'
import type { Lap } from '../../domain/lap/Lap'
import type { LapUnreliableReason } from '../../telemetryChecks/lapChecks/lapReliability'
import { fromTaggedJson, toTaggedJson } from '../taggedJson'
import {
  isReadableProcessingVersion,
  PROCESSING_VERSION,
  PROCESSING_VERSION_KEY,
} from '../processingVersion'
import { SummaryFormatError } from './summaryFormatError'
import { hasSummaryShape } from './summaryShape'

export interface SummaryLap {
  lapId: string
  lapNumber: number
  lapTimeMs: number
  excludedByDefault: boolean
  unreliableReasons: LapUnreliableReason[]
}

export interface StoredZoneAnalysis {
  zones: TrackZone[]
  focusZoneIds: string[]
  improveZoneIds: string[]
  solidZoneIds: string[]
  zoneLapData: Record<string, ZoneLapData[]>
  trackStats: TrackStats
  trackGradient: ZoneVariancePoint[] | null
}

export interface SessionSummary {
  versionKey: string
  laps: SummaryLap[]
  analysis: StoredZoneAnalysis
}

export function buildSessionSummary(
  sessionLaps: Lap[],
  storedLapNumbers: number[]
): SessionSummary {
  const laps = selectStoredLaps(sessionLaps, storedLapNumbers)
  const { excludedLapIds, analysis } = defaultSessionView(laps)
  return {
    versionKey: PROCESSING_VERSION_KEY,
    laps: laps.map((lap) => ({
      lapId: lap.id,
      lapNumber: lap.lapNumber,
      lapTimeMs: lap.lapTime,
      excludedByDefault: excludedLapIds.has(lap.id),
      unreliableReasons: [...lap.unreliableReasons],
    })),
    analysis: storedZoneAnalysis(analysis),
  }
}

export function defaultExcludedLapIds(summary: SessionSummary): Set<string> {
  return new Set(
    summary.laps.filter((lap) => lap.excludedByDefault).map((lap) => lap.lapId)
  )
}

export function restoreZoneAnalysis(
  summary: SessionSummary
): ZoneAnalysisResult {
  const { analysis } = summary
  const zones = analysis.zones.map(withLowestRowLinked)
  const zonesById = new Map(zones.map((zone) => [zone.id, zone]))
  const zonesNamed = (ids: string[]) =>
    ids.map((id) => {
      const zone = zonesById.get(id)
      if (!zone) {
        throw new SummaryFormatError(
          `the summary names zone ${id}, which it lacks`
        )
      }
      return zone
    })
  return {
    zones,
    focusZones: zonesNamed(analysis.focusZoneIds),
    improveZones: zonesNamed(analysis.improveZoneIds),
    solidZones: zonesNamed(analysis.solidZoneIds),
    zoneLapData: new Map(Object.entries(analysis.zoneLapData)),
    trackStats: analysis.trackStats,
    trackGradient: analysis.trackGradient,
  }
}

export function encodeSessionSummary(summary: SessionSummary): string {
  return toTaggedJson(summary)
}

export function decodeSessionSummary(text: string): SessionSummary {
  const summary = parsed(text) as Partial<SessionSummary> | null
  if (
    typeof summary?.versionKey !== 'string' ||
    !isReadableProcessingVersion(summary.versionKey)
  ) {
    throw new SummaryFormatError(
      `summary version ${summary?.versionKey}, expected major ${PROCESSING_VERSION.major}`
    )
  }
  if (!hasSummaryShape(summary.laps, summary.analysis)) {
    throw new SummaryFormatError(
      'the summary’s laps or analysis are missing or malformed'
    )
  }
  return summary as SessionSummary
}

function storedZoneAnalysis(analysis: ZoneAnalysisResult): StoredZoneAnalysis {
  const ids = (zones: TrackZone[]) => zones.map((zone) => zone.id)
  return {
    zones: analysis.zones,
    focusZoneIds: ids(analysis.focusZones),
    improveZoneIds: ids(analysis.improveZones),
    solidZoneIds: ids(analysis.solidZones),
    zoneLapData: Object.fromEntries(analysis.zoneLapData),
    trackStats: analysis.trackStats,
    trackGradient: analysis.trackGradient,
  }
}

function withLowestRowLinked(zone: TrackZone): TrackZone {
  const percent = zone.consistencyPercent
  if (!percent?.lowest) return zone
  const lowest = percent.rows.find((row) => row.isLowest) ?? percent.lowest
  return { ...zone, consistencyPercent: { ...percent, lowest } }
}

function parsed(text: string): unknown {
  try {
    return fromTaggedJson(text)
  } catch {
    throw new SummaryFormatError('the summary is not valid JSON')
  }
}
